import { API_URLS } from 'constants/index';
import {
  JSONApiResponseElement,
  JSONApiResponse,
  ModelTypes,
  QueryParams,
  GeneralIds,
} from 'types/types';
import { UserSlim } from './UserSlim';

export const denormalize = (data: any) => {
  const res = data;
  for (const key in res) {
    if (Array.isArray(res[key])) {
      res[key] = res[key].map((el: any) =>
        // if contains uuid we have to remove it and return ID only as it's JSON API compliant
        // if it has el.fieldType then we have to keep the ID because field template IDs are generated on the client side
        el.id?.length === 36 && !el.fieldType ? el.id : el,
      );
    }
  }
  const denormalized = (({ id, ...o }) => o)(res);

  return denormalized;
};

export const normalize = ({ data, included }: JSONApiResponse) => {
  if (!Array.isArray(data)) return normalizeOne(data, included);
  const res = data.map((el: any) => normalizeOne(el, included));
  return res;
};

export const normalizeOne = (
  data: JSONApiResponseElement,
  included?: JSONApiResponseElement[],
) => {
  const relationship = data.relationships || {};
  const obj: any = {
    ...data.attributes,
    id: data.id,
  };

  for (const relation in relationship) {
    if (!Array.isArray(relationship[relation].data)) {
      // @ts-ignore
      const relationshipId = relationship[relation].data?.id;
      const linked = included?.find((incl: any) => incl.id === relationshipId);
      obj[relation] = {
        ...linked?.attributes,
        id: linked?.id,
      };
    } else {
      obj[relation] = relationship[relation].data.map((el: any) => {
        const linked = included?.find((incl: any) => incl.id === el.id);
        return {
          ...linked?.attributes,
          id: linked?.id,
        };
      });
    }
  }

  for (const includedEl of included ?? []) {
    // this is needed for updatedBy and createdBy fields
    // we need to fill in the user not just the email address
    const slim = includedEl.attributes as UserSlim;
    if (
      includedEl.type === 'userSlim' &&
      obj.updatedBy &&
      slim.email === obj.updatedBy
    )
      obj.slimUser = includedEl.attributes;

    // if the entity is `form` we need to add the included fields for the visibility prop
    // sometimes the referenced field is not in the same form
    if (data.type === 'form') {
      if (includedEl.type === 'fieldTemplate') {
        const template = {
          ...includedEl.attributes,
          id: includedEl.id,
        };

        if (obj.externalFieldTemplates) {
          obj.externalFieldTemplates.push(template as any);
        } else {
          obj.externalFieldTemplates = [template as any];
        }
      }
      if (includedEl.type === 'field') {
        if (obj.externalFields) {
          obj.externalFields.push(slim as any);
        } else {
          obj.externalFields = [slim as any];
        }
      }
    }
  }

  return obj;
};

export const getURLByModelType = (
  type: ModelTypes,
  { id, projectId, patientId, visitId, formTemplateTypeId }: GeneralIds = {},
) => {
  const basicUrl = `${type}/${id ?? ''}`;
  const url = API_URLS[type] ?? basicUrl;

  return url
    .replace(':projectId', projectId ?? '')
    .replace(':id', id ?? '')
    .replace(':patientId', patientId ?? '')
    .replace(':formTemplateTypeId', formTemplateTypeId ?? '')
    .replace(':visitId', visitId ?? '')
    .replace(/\/$/, '');
};

export const getQueryParams = (queryParams?: QueryParams) => {
  if (!queryParams) return '';
  const res = Object.entries(queryParams)
    .filter(([key, value]) => (value ? true : false))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  return `?${res}`;
};
